import { motion } from "framer-motion";
import { Instagram, Package, Recycle, Scissors } from "lucide-react";

const IG_URL = "https://instagram.com/grill_enforcers";

const STEPS = [
    {
        icon: Package,
        title: "SEND US YOUR STICK",
        text: "Game-used, snapped in the playoffs, or collecting dust in the garage — ship it or drop it off.",
    },
    {
        icon: Scissors,
        title: "WE BUILD YOUR ENFORCER",
        text: "Same heavy serrated blade, same four lace options — but the handle is your stick, your scars, your story.",
    },
    {
        icon: Recycle,
        title: "WE KEEP THE OFFCUTS",
        text: "Whatever parts of the stick we don't use stay with us for future builds. That's the deal — still just $40.",
    },
];
const fadeUp = {
    hidden: { opacity: 0, y: 40 },
    show: (i = 0) => ({
        opacity: 1,
        y: 0,
        transition: { delay: i * 0.12, duration: 0.8, ease: [0.16, 1, 0.3, 1] },
    }),
};

export default function CustomSticks() {
    return (
        <section
            data-testid="custom-sticks-section"
            className="relative border-t border-[#2A2A36] bg-[#0D0D11] px-5 py-20 sm:px-10 sm:py-28 lg:px-16"
        >
            <div className="noise-layer" />
            <div className="mx-auto max-w-[1380px]">
                <motion.p
                    variants={fadeUp}
                    initial="hidden"
                    whileInView="show"
                    viewport={{ once: true }}
                    className="font-jbmono mb-6 text-[10px] uppercase tracking-[0.3em] text-[#FFD60A] sm:text-xs"
                >
                    02 — CUSTOM STICKS
                </motion.p>
                <div className="flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between">
                    <motion.h2
                        variants={fadeUp}
                        custom={1}
                        initial="hidden"
                        whileInView="show"
                        viewport={{ once: true }}
                        className="font-cond max-w-2xl text-4xl font-extrabold uppercase leading-[0.9] tracking-tight text-[#F2F2F7] sm:text-6xl"
                    >
                        Your stick.
                        <br />
                        <span className="text-outline-ember">Your Enforcer.</span>
                    </motion.h2>
                    <motion.p
                        variants={fadeUp}
                        custom={2}
                        initial="hidden"
                        whileInView="show"
                        viewport={{ once: true }}
                        className="max-w-md text-sm leading-relaxed text-[#A1A1B2] sm:text-base"
                    >
                        Send us your own hockey stick and we'll forge it into a one-of-one
                        Enforcer — still <span className="font-semibold text-[#F2F2F7]">$40</span>.
                        We keep whatever parts of the stick we don't use. Sort out the details
                        in our DMs.
                    </motion.p>
                </div>

                <div className="mt-14 grid gap-px border border-[#2A2A36] bg-[#2A2A36] lg:grid-cols-3">
                    {STEPS.map((s, i) => (
                        <motion.article
                            key={s.title}
                            data-testid={`custom-step-${i + 1}`}
                            variants={fadeUp}
                            custom={i}
                            initial="hidden"
                            whileInView="show"
                            viewport={{ once: true, margin: "-60px" }}
                            className="group bg-[#0A0A0C] p-8 transition-colors duration-300 hover:bg-[#121216] sm:p-10"
                        >
                            <div className="flex items-center justify-between">
                                <s.icon
                                    className="h-7 w-7 text-[#FF3B30] transition-transform duration-300 group-hover:-translate-y-1"
                                    strokeWidth={2}
                                />
                                <span className="font-cond text-outline text-5xl font-extrabold leading-none">
                                    {String(i + 1).padStart(2, "0")}
                                </span>
                            </div>
                            <h3 className="font-cond mt-6 text-xl font-bold uppercase tracking-wide text-[#F2F2F7] sm:text-2xl">
                                {s.title}
                            </h3>
                            <p className="mt-3 text-sm leading-relaxed text-[#A1A1B2]">{s.text}</p>
                        </motion.article>
                    ))}
                </div>

                <motion.div
                    variants={fadeUp}
                    custom={3}
                    initial="hidden"
                    whileInView="show"
                    viewport={{ once: true }}
                    className="mt-9 flex flex-col gap-3 sm:flex-row"
                >
                    <a
                        href={IG_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="custom-sticks-dm-button"
                        className="flex flex-1 items-center justify-center gap-3 border-2 border-[#FF3B30] bg-[#FF3B30] px-6 py-3.5 font-cond text-xl font-bold uppercase tracking-wide text-[#0A0A0C] transition-[transform,background-color,color] duration-150 hover:-translate-y-0.5 hover:bg-transparent hover:text-[#FF3B30]"
                    >
                        <Instagram className="h-4 w-4" strokeWidth={2.5} />
                        DM ABOUT A CUSTOM STICK
                    </a>

                </motion.div>
            </div>
        </section>
    );
}
